import React from 'react';
import { Tooltip } from 'react-tooltip';
import Tag from '@app/component/tag/Tag';
import Utilities from '@util/Utilities';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import {
	describePriceRecommendationLimit,
	parsePriceRecommendationLimit,
	removeRoomLimitParts,
	PriceRecommendationLimitText,
} from '@app/model/market_state/PriceRecommendationLimit';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';

/**
 * The Limit Explanation of a Limited Recommendation (a Screen Part): the amber tag, the two lines
 * of the drawer, and the hover card of the day popup. One source of text, so the two never drift.
 *
 * A recommendation is limited only when the processor sets priceRuleLimited. The app parses it
 * (PriceRecommendationLimit). Unknown shapes show the stored text with room-limit parts removed.
 */

export type RecommendationLimitView = {
	limited: boolean,
	text: PriceRecommendationLimitText,
	// The remaining narrative, shown when the parser does not know the shape.
	rawText?: string,
};

export const useRecommendationLimit = (recommendationState: PriceRecommendationState | undefined): RecommendationLimitView | undefined => {
	const { companyHotel } = useSelectedCompanyHotel();
	if (recommendationState === undefined) return undefined;
	const narrative = removeRoomLimitParts(recommendationState.narrative) ?? '';
	const limited = recommendationState.priceRuleLimited === true;
	const hasNarrative = narrative.trim().length > 0;
	if (!limited && !hasNarrative) return undefined;

	const limit = limited ? parsePriceRecommendationLimit(narrative) : undefined;
	if (limit === undefined) {
		return {
			limited,
			text: {
				tag: 'Price rule', tagShort: 'Price rule', title: 'Set by your price rule',
				ruleLine: narrative, engineLine: '', detailLine: '',
			},
			rawText: hasNarrative ? narrative : 'Your price rule adjusted this recommendation.',
		};
	}
	return {
		limited,
		text: describePriceRecommendationLimit(limit, {
			formatAmount: (amount) => Utilities.formatAmount(amount, companyHotel.settings.main.currency, 0),
			roomTypeLabel: Utilities.snakeCaseToWords(recommendationState.roomMappingType),
		}),
	};
};

export const RecommendationLimitTag: React.FunctionComponent<{
	view: RecommendationLimitView,
	short?: boolean,
	extraClassName?: string,
	tooltipId?: string,
}> = ({ view, short, extraClassName, tooltipId }) => {
	if (!view.limited) return null;
	const label = short === true ? view.text.tagShort : view.text.tag;
	if (tooltipId !== undefined) {
		return <button
			type="button"
			aria-label={view.text.title}
			data-tooltip-id={tooltipId}
			className={Utilities.classNames('group inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1', extraClassName ?? '')}>
			<Tag state="warning">{label}</Tag>
		</button>;
	}
	return <Tag state="warning" extraClassName={extraClassName}>{label}</Tag>;
};

/** The two lines. Line 1: the percent of the rule, then the engine percent in grey. Line 2: the bound and its source. */
export const RecommendationLimitLines: React.FunctionComponent<{ view: RecommendationLimitView }> = ({ view }) => {
	if (view.rawText !== undefined) {
		return <p className="text-xs font-medium leading-5 text-gray-700">{view.rawText}</p>;
	}
	return <>
		<p className="text-xs font-semibold leading-5 text-gray-900">
			{view.text.ruleLine}
			<span className="font-normal text-gray-400"> · {view.text.engineLine}</span>
		</p>
		<p className="mt-0.5 text-xs leading-5 text-gray-700">{view.text.detailLine}</p>
	</>;
};

/** The hover card of the day popup: the pattern of the factor tooltips (ROO2-46). Hover and focus on a pointer, tap on touch. */
export const RecommendationLimitTooltip: React.FunctionComponent<{ id: string, view: RecommendationLimitView }> = ({ id, view }) => {
	if (!view.limited) return null;
	// The card lives inside the day popup, which is a positioned tooltip itself. The fixed strategy
	// places the card against the viewport, so the positioned ancestor does not shift it.
	return <Tooltip
		id={id}
		place="right"
		positionStrategy="fixed"
		openEvents={{ mouseenter: true, focus: true, click: true }}
		closeEvents={{ mouseleave: true, blur: true }}
		globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
		className="z-50 !rounded-lg !bg-white !p-0 !opacity-100 shadow-xl ring-1 ring-black ring-opacity-5"
		style={{ backgroundColor: 'white' }}>
		<div className="w-72 overflow-hidden rounded-lg bg-white px-4 py-3 text-left text-xs text-gray-600">
			<p className="text-sm font-semibold text-gray-900">{view.text.title}</p>
			<div className="mt-2 border-t border-gray-200 pt-2">
				<RecommendationLimitLines view={view} />
			</div>
		</div>
	</Tooltip>;
};
